import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Observable, tap } from 'rxjs';
import { ConfirmService } from '../../../shared/services/confirm.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { SingleResponseModel } from '../../../shared/models/singleResponse.model';
import { CustomerPortalLink } from '../../models/customer-portal.models';
import { CustomerPortalService } from '../../services/customer-portal.service';

export interface ContactPortalDialogData {
  contactUid: string;
  contactName?: string;
}

/**
 * "Portal del cliente" de un contacto: activar el portal y generar, copiar, regenerar o revocar el
 * enlace que se le da al cliente para ver y descargar sus facturas. Se abre como popup desde el menú
 * ⋮ del listado de contactos y también como pestaña de la ficha (contact/:contactUid/portal).
 */
@Component({
  selector: 'app-contact-portal',
  templateUrl: './contact-portal.component.html',
  styleUrls: ['./contact-portal.component.scss'],
  standalone: false,
})
export class ContactPortalComponent implements OnInit {
  contactUid: string | null = null;

  loading = true;
  loadError = false;
  working = false;

  enabled = false;
  link: CustomerPortalLink | null = null;

  static open(dialog: MatDialog, data: ContactPortalDialogData): Observable<void> {
    return dialog.open<ContactPortalComponent, ContactPortalDialogData>(ContactPortalComponent, {
      width: '640px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed();
  }

  constructor(
    private route: ActivatedRoute,
    private portalService: CustomerPortalService,
    private clipboard: Clipboard,
    private confirm: ConfirmService,
    private notification: NotificationService,
    @Optional() public dialogRef: MatDialogRef<ContactPortalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ContactPortalDialogData,
  ) {}

  ngOnInit(): void {
    // En popup el uid llega en los datos; como pestaña cuelga de contact/:contactUid/portal y está
    // más arriba del árbol de rutas.
    this.contactUid = this.data?.contactUid
      ?? this.route.pathFromRoot.map(r => r.snapshot.paramMap.get('contactUid')).find(uid => !!uid)
      ?? null;

    this.load();
  }

  get isDialog(): boolean {
    return !!this.dialogRef;
  }

  close(): void {
    this.dialogRef?.close();
  }

  get url(): string {
    return this.link ? this.portalService.publicUrl(this.link.token) : '';
  }

  load(): void {
    if (!this.contactUid) {
      this.loading = false;
      this.loadError = true;
      return;
    }

    this.loading = true;
    this.loadError = false;

    forkJoin({
      settings: this.portalService.getSettings(),
      link: this.portalService.getLink(this.contactUid),
    }).subscribe({
      next: ({ settings, link }) => {
        this.enabled = settings.value?.enabled === true;
        this.link = link.value ?? null;
        this.loading = false;
      },
      error: () => {
        this.loadError = true;
        this.loading = false;
      },
    });
  }

  enablePortal(): void {
    this.working = true;

    this.portalService.saveSettings(true).subscribe({
      next: () => {
        this.enabled = true;
        this.working = false;
        this.notification.ok('Portal del cliente activado');
      },
      error: err => {
        this.working = false;
        this.notification.error(this.errorMessage(err, 'No se ha podido activar el portal'));
      },
    });
  }

  disablePortal(): void {
    this.confirm.open({
      title: 'Desactivar el portal del cliente',
      message: 'Ningún cliente podrá abrir su enlace mientras el portal esté desactivado. Los enlaces no se borran: vuelven a funcionar al activarlo de nuevo.',
      icon: 'block',
      tone: 'warning',
      confirmText: 'Desactivar',
      loadingText: 'Desactivando…',
      errorText: 'No se ha podido desactivar el portal. Inténtalo de nuevo.',
      action: () => this.portalService.saveSettings(false).pipe(tap(() => this.enabled = false)),
    }).subscribe(ok => {
      if (ok) {
        this.notification.ok('Portal del cliente desactivado');
      }
    });
  }

  createLink(): void {
    this.working = true;

    this.saveLink().subscribe({
      next: () => {
        this.working = false;
        this.notification.ok('Enlace creado');
      },
      error: err => {
        this.working = false;
        this.notification.error(this.errorMessage(err, 'No se ha podido crear el enlace'));
      },
    });
  }

  regenerateLink(): void {
    this.confirm.open({
      title: 'Regenerar el enlace',
      message: 'Se crea un enlace nuevo y el actual deja de funcionar. Tendrás que enviarle el nuevo al cliente.',
      icon: 'refresh',
      tone: 'warning',
      confirmText: 'Regenerar',
      loadingText: 'Regenerando…',
      errorText: 'No se ha podido regenerar el enlace. Inténtalo de nuevo.',
      action: () => this.saveLink(),
    }).subscribe(ok => {
      if (ok) {
        this.notification.ok('Enlace regenerado; el anterior ya no funciona');
      }
    });
  }

  revokeLink(): void {
    this.confirm.open({
      title: 'Revocar el enlace',
      message: 'El cliente dejará de poder ver sus facturas con este enlace. Puedes crear otro cuando quieras.',
      icon: 'block',
      tone: 'danger',
      confirmText: 'Revocar',
      loadingText: 'Revocando…',
      errorText: 'No se ha podido revocar el enlace. Inténtalo de nuevo.',
      action: () => this.portalService.revokeLink(this.contactUid!).pipe(tap(() => this.link = null)),
    }).subscribe(ok => {
      if (ok) {
        this.notification.ok('Enlace revocado');
      }
    });
  }

  copyLink(): void {
    if (this.clipboard.copy(this.url)) {
      this.notification.ok('Enlace copiado');
    } else {
      this.notification.error('No se ha podido copiar el enlace');
    }
  }

  openLink(): void {
    window.open(this.url, '_blank', 'noopener');
  }

  private saveLink(): Observable<SingleResponseModel<CustomerPortalLink>> {
    return this.portalService.createLink(this.contactUid!).pipe(tap(res => this.link = res.value));
  }

  private errorMessage(err: any, fallback: string): string {
    return err?.error?.error?.messages?.find((m: string) => !!m) ?? fallback;
  }
}
